import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { noetivraRoadmap, roadmapAreas } from '../data/research';
import { easeOutStrong, fadeUp } from '../utils/motion';
export function RoadmapPage() {
  const scrollToArea = (id: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    document.getElementById(id)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  return <>
      <section className="bg-white border-b border-line pt-32 pb-20 lg:pt-44 lg:pb-24" data-id="element-2119" data-mp="dklpi0l7dxqx">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: easeOutStrong
      }} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-2120" data-mp="ijgifvcvx2or">
          <div className="lg:col-span-5" data-id="element-2121" data-mp="2oc54s2nkophx">
            <p className="text-sm font-medium text-teal mb-5" data-id="element-2122" data-mp="2t95845t1fe5c">Roadmap</p>
            <h1 className="font-heading font-extrabold text-5xl lg:text-6xl text-ink-dark leading-[1.05] tracking-tight" data-id="element-2123" data-mp="2tb8alpbdmzqy">
              Where SyNeural Is Going
            </h1>
            <p className="mt-5 text-lg text-ink-muted" data-id="element-2124" data-mp="2c31rg9e9e5cw">
              Product development, research, and pilot priorities.
            </p>
          </div>
          <div className="lg:col-span-7 lg:pt-10" data-id="element-2125" data-mp="234gfix3alol3">
            <p className="text-2xl text-ink-dark leading-relaxed mb-6" data-id="element-2126" data-mp="235riiuowysb5">
              SyNeural studies how people develop sound judgment, how
              researchers connect ideas, and how institutions turn knowledge
              into responsible action.
            </p>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-2127" data-mp="2qs7ru0ou21md">
              This roadmap covers planned work on Noetivra, the Semantic
              Knowledge Platform (SKP), and the research agenda behind CJI and
              NCJI. Each item describes work we intend to explore, build, test,
              and refine—not a completed milestone, a release date, or a claim
              that every proposed measure or application has been validated.
            </p>
          </div>
        </motion.div>
      </section>

      <section aria-labelledby="noetivra-roadmap" className="bg-white py-20 lg:py-28" data-id="element-2128" data-mp="1guq4bje8oxym">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-2129" data-mp="12v8bmsxtp0jz">
          <motion.div {...fadeUp} className="lg:col-span-4" data-id="element-2130" data-mp="17wxpegcfjsbr">
            <p className="text-sm font-medium text-teal mb-3" data-id="element-2131" data-mp="t4vtt8he7lhj">Planned product work</p>
            <h2 id="noetivra-roadmap" className="font-heading font-bold text-3xl lg:text-4xl text-ink-dark tracking-tight leading-tight mb-5" data-id="element-2132" data-mp="xwx26jbfws2a">
              Building Noetivra
            </h2>
            <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-2133" data-mp="o7dgz7098m49">
              Noetivra is in development. This work is planned; it is not listed
              in a committed order or schedule.
            </p>
            <a href="/noetivra" className="inline-flex items-center gap-2 text-navy font-semibold hover:text-navy-light transition-colors duration-150 whitespace-nowrap" data-id="element-2134" data-mp="f88k054cd0u7">
              About Noetivra
              <ArrowRight size={16} aria-hidden="true" data-id="element-2135" data-mp="5j31hay69ejg" />
            </a>
          </motion.div>
          <motion.dl {...fadeUp} className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-x-12 border-t border-line" data-id="element-2136" data-mp="ixggdmeqqac">
            {noetivraRoadmap.map(item => <div key={item.title} className="border-b border-line py-7" data-id="element-2137" data-mp="9kolrhd1cv3o">
                <dt className="font-heading font-bold text-xl text-ink-dark mb-2" data-id="element-2138" data-mp="o91iigjy6hz8">{item.title}</dt>
                <dd className="text-ink-muted leading-relaxed" data-id="element-2139" data-mp="j9ycotw587q7">{item.body}</dd>
              </div>)}
          </motion.dl>
        </div>
      </section>

      <section className="bg-surface-bg py-20 lg:py-28" data-id="element-2140" data-mp="xa92rupmpiab">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-2141" data-mp="t8nvqpzrzy81">
          <nav aria-label="Roadmap directions" className="hidden lg:block lg:col-span-4" data-id="element-2142" data-mp="2mptm9y01x4em">
            <div className="sticky top-28" data-id="element-2143" data-mp="2rp65m8rprtcg">
              <p className="text-sm font-medium text-ink-muted mb-4" data-id="element-2144" data-mp="2rnv3sehtwvnz">
                {roadmapAreas.length} research directions
              </p>
              <ul className="border-l border-line" data-id="element-2145" data-mp="31g96pakn88bq">
                {roadmapAreas.map(area => <li key={area.id} data-id="element-2146" data-mp="23siqindmot3x">
                    <a href={`#${area.id}`} onClick={scrollToArea(area.id)} className="block -ml-px border-l-2 border-transparent hover:border-navy pl-4 py-1.5 text-sm text-ink-muted hover:text-navy transition-colors duration-150" data-id="element-2147" data-mp="23tk1ysyk3hm6">
                      {area.title}
                    </a>
                  </li>)}
              </ul>
            </div>
          </nav>

          <div className="lg:col-span-8" data-id="element-2148" data-mp="3t65k1xyyok9o">
            {roadmapAreas.map(area => <motion.article key={area.id} id={area.id} {...fadeUp} className="scroll-mt-28 border-t border-line py-12 first:border-t-0 first:pt-0" data-id="element-2149" data-mp="1hk3fkzitas3t">
                <h2 className="font-heading font-bold text-3xl text-ink-dark tracking-tight mb-5" data-id="element-2150" data-mp="18pyt0sapo2u3">
                  {area.title}
                </h2>
                <p className="text-xl text-ink-dark leading-relaxed mb-5" data-id="element-2151" data-mp="yt2g8ads4ujq">{area.lead}</p>
                <div className="space-y-4" data-id="element-2152" data-mp="yqpvjpgn062z">
                  {area.paragraphs.map(paragraph => <p key={paragraph} className="text-lg text-ink-muted leading-relaxed" data-id="element-2153" data-mp="pse5bojonab2">
                      {paragraph}
                    </p>)}
                </div>
              </motion.article>)}
          </div>
        </div>
      </section>

      <section className="bg-navy text-white py-20 lg:py-28" data-id="element-2154" data-mp="2k4ce5pe2y46f">
        <motion.div {...fadeUp} className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2155" data-mp="2f8dl3oht3xzo">
          <h2 className="font-heading font-bold text-4xl lg:text-5xl tracking-tight mb-6" data-id="element-2156" data-mp="2yaw5h7e8apvi">
            From roadmap to responsible application
          </h2>
          <p className="text-xl text-white/80 leading-relaxed mb-10" data-id="element-2157" data-mp="2pb958ox8jq41">
            SyNeural’s work connects three activities: organizing knowledge,
            developing human capability, and improving institutional decisions.
            SKP helps connect and govern what is known. CJI and NCJI explore how
            people develop judgment. Noetivra would make both usable through an
            interactive workspace. Pilots and independent review test whether
            the resulting tools work as intended.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2158" data-mp="3d022coplg408">
            <a href="/research" className="inline-flex items-center justify-center gap-2 bg-white text-navy hover:bg-surface-muted font-semibold px-6 py-3.5 rounded-md transition-colors duration-150 whitespace-nowrap" data-id="element-2159" data-mp="1wmv4i1erifos">
              Explore the Research Agenda
              <ArrowRight size={18} data-id="element-2160" data-mp="1p3ce3a36vk66" />
            </a>
            <a href="/partnerships" className="inline-flex items-center justify-center border border-white/40 text-white hover:bg-white/10 font-semibold px-6 py-3.5 rounded-md transition-colors duration-150 whitespace-nowrap" data-id="element-2161" data-mp="1p2kmsbgmk8lp">
              Discuss a Research Partnership
            </a>
          </div>
        </motion.div>
      </section>
    </>;
}